import React, { useState } from 'react';
import { BookIcon, LibraryIcon } from 'lucide-react';
import { XIcon } from 'lucide-react';
import { mockBooks, merchCategories, eventTypes, serviceProviderRoles, genreOptions } from '../../../data/productManagerData';
import { SearchWithAutocomplete, Suggestion } from '../../../components/UI/SearchWithAutocomplete';
import { SUBGENRE_SUGGESTIONS } from './genreModuleData';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import type { PM } from './useProductManager';
// Build genre suggestions for secondary genre search
const genreSuggestions: Suggestion[] = genreOptions.map(g => ({
  id: g.toLowerCase().replace(/\s+/g, '-'),
  label: g,
  type: 'Genre'
}));
interface BasicInfoSectionProps {
  pm: PM;
}
export const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  pm
}) => {
  const rt = useRoleTheme();
  const dk = useDarkClasses();
  const {
    bookForm,
    setBookForm
  } = pm;
  const {
    productType,
    connectionType,
    setConnectionType,
    seriesStatus,
    setSeriesStatus,
    merchForm,
    setMerchForm,
    eventForm,
    setEventForm,
    bookBoxForm,
    setBookBoxForm,
    creativeForm,
    setCreativeForm,
    serviceForm,
    setServiceForm,
    secondaryGenres,
    setSecondaryGenres,
    entryPoint,
    setEntryPoint
  } = pm;
  const [anthologyConnection, setAnthologyConnection] = useState('');
  const [anthologySearch, setAnthologySearch] = useState('');
  const [secondaryGenreSearch, setSecondaryGenreSearch] = useState('');
  return <div className="space-y-8" data-id="element-19937" data-mp="6zqtf0z4i9ia">
      <div data-id="element-19938" data-mp="70sfe0hogges">
        <h2 className={`text-lg font-bold mb-4 flex items-center ${dk.textHeading}`} data-id="element-19939" data-mp="70iq6f4gida5">
          <BookIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-19940" data-mp="228rhujiwujs" />
          Basic Information
        </h2>
        <div className="space-y-4" data-id="element-19941" data-mp="3u3x9emrr3lyl">
          {productType === 'book' && <>
              <div data-id="element-19942" data-mp="3p5vpjzmi3im2">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19943" data-mp="3p7gi5jp98rha">
                  Book Title
                </label>
                <input type="text" value={bookForm.title || ''} onChange={e => setBookForm({
              ...bookForm,
              title: e.target.value
            })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Enter book title" data-id="element-19944" data-mp="3jkwin6xvaaf1" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-19945" data-mp="3oiy05nfna1oe">
                <div data-id="element-19946" data-mp="3oglk5ftg61dz">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19947" data-mp="3th9as0drkg7o">
                    Author Name
                  </label>
                  <input type="text" value={bookForm.author || ''} onChange={e => setBookForm({
                ...bookForm,
                author: e.target.value
              })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Author name" data-id="element-19948" data-mp="psfqeokq9is2" />
                </div>

                {/* Primary Genre */}
                <div data-id="element-19949" data-mp="1rjgyi3gt0c02">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19950" data-mp="1rl0h40pxmz50">
                    Primary Genre <span className="text-red-500" data-id="element-19951" data-mp="1mn8ot5ldlb0a">*</span>
                  </label>
                  <select value={bookForm.genre || ''} onChange={e => {
                const newGenre = e.target.value;
                setBookForm({
                  ...bookForm,
                  genre: newGenre
                });
                // Clean up: remove new primary from secondary genres
                if (newGenre && secondaryGenres.includes(newGenre)) {
                  setSecondaryGenres(secondaryGenres.filter(sg => sg !== newGenre));
                }
              }} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-19952" data-mp="20ikh76c26pe1">
                    <option value="" data-id="element-19953" data-mp="p55nfw7rnqob">Select Primary Genre</option>
                    {genreOptions.map(g => <option key={g} value={g} data-id="element-19954" data-mp="u25vgco6f3i2">
                        {g}
                      </option>)}
                  </select>
                </div>

                {/* Sub-genre Search */}
                {bookForm.genre && <div className="sm:col-span-2" data-id="element-19955" data-mp="18sd1fswzyu1v">
                    <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19956" data-mp="18txzhsiisv4h">
                      Sub-genre
                    </label>
                    <SearchWithAutocomplete value={pm.subGenreSearch} onChange={pm.setSubGenreSearch} suggestions={SUBGENRE_SUGGESTIONS[bookForm.genre] || []} placeholder={`Search ${bookForm.genre} sub-genres...`} onSelect={item => {
                pm.setSelectedSubGenres((prev: string[]) => prev.includes(item.label) ? prev.filter((g: string) => g !== item.label) : [...prev, item.label]);
                pm.setSubGenreSearch('');
              }} data-id="element-19957" data-mp="2xcsgys0brutw" />
                    {pm.selectedSubGenres.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-19958" data-mp="3ld8d2i9oy09">
                        {pm.selectedSubGenres.map((sub: string) => <span key={sub} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${dk.darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-900'}`} data-id="element-19959" data-mp="3uygclaoyi94d">
                            {sub}
                            <button onClick={() => pm.setSelectedSubGenres((prev: string[]) => prev.filter((g: string) => g !== sub))} className="ml-1.5 hover:opacity-75" data-id="element-19960" data-mp="1ob8wrutggzqj">
                              ×
                            </button>
                          </span>)}
                      </div>}
                  </div>}

                <div data-id="element-19961" data-mp="2zr7cuin1wm19">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19962" data-mp="2zpmhwrwtr12v">
                    Series (Optional)
                  </label>
                  <div className="flex gap-2" data-id="element-19963" data-mp="34wkkjip97gsv">
                    <input type="text" value={bookForm.series?.name || ''} onChange={e => setBookForm({
                  ...bookForm,
                  series: {
                    ...bookForm.series,
                    name: e.target.value,
                    position: bookForm.series?.position || 1
                  }
                })} className={`flex-1 p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Series name" data-id="element-19964" data-mp="34tolts5l0o3d" />
                    <input type="number" value={bookForm.series?.position || ''} onChange={e => setBookForm({
                  ...bookForm,
                  series: {
                    ...bookForm.series,
                    name: bookForm.series?.name || '',
                    position: parseInt(e.target.value)
                  }
                })} className={`w-20 p-2 rounded-md border ${dk.inputOnCard}`} placeholder="#" data-id="element-19965" data-mp="2789dlvap8xic" />
                  </div>
                </div>
              </div>

              {/* Expanded Series Details — shown when series name is set */}
              {bookForm.series?.name && <div className={`rounded-lg border p-4 space-y-4 ${dk.border} ${dk.surfaceMuted}`} data-id="element-19966" data-mp="2msivmcapxqs6">
                  <div className="flex items-center gap-2 mb-1" data-id="element-19967" data-mp="3t7jtinjb0j1e">
                    <LibraryIcon className="h-4 w-4 text-indigo-500" data-id="element-19968" data-mp="218wd2hf02fc" />
                    <span className={`text-sm font-semibold ${dk.textSecondary}`} data-id="element-19969" data-mp="23lbl7vsfyop">
                      Series Details
                    </span>
                  </div>

                  {/* Total books + Series Status */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-19970" data-mp="2ecieypjcjah">
                    <div data-id="element-19971" data-mp="3uin8nssh9u8k">
                      <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19972" data-mp="3ujg6235jr7e1">
                        Total Books in Series
                      </label>
                      <input type="number" value={(bookForm.series as any)?.totalBooks || ''} onChange={e => setBookForm({
                  ...bookForm,
                  series: {
                    ...bookForm.series,
                    name: bookForm.series?.name || '',
                    position: bookForm.series?.position || 1,
                    totalBooks: parseInt(e.target.value) || undefined
                  } as any
                })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="e.g. 3" min={1} data-id="element-19973" data-mp="3knv9ikj8yzbo" />
                    </div>
                    <div data-id="element-19974" data-mp="2tjmzfgnsb58j">
                      <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-19975" data-mp="2tgr1vtfumikm">
                        Series Status
                      </label>
                      <select value={seriesStatus} onChange={e => setSeriesStatus(e.target.value)} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-19976" data-mp="2yi6r7xp27b8t">
                        <option value="" data-id="element-19977" data-mp="25szqqt61obkx">Select status</option>
                        <option value="ongoing" data-id="element-19978" data-mp="lays48mts0hj">Ongoing</option>
                        <option value="complete" data-id="element-19979" data-mp="l9njnvifq4k3">Complete</option>
                        <option value="backlist" data-id="element-19980" data-mp="q3rsylf6aooc">Backlist</option>
                        <option value="hiatus" data-id="element-19981" data-mp="cbbuzz14pgua">Hiatus</option>
                        <option value="cancelled" data-id="element-19982" data-mp="c8z611wye67q">Discontinued</option>
                      </select>
                    </div>
                  </div>
                </div>}

              {/* Connection Type & Anthology — always visible for books */}
              <div className={`rounded-lg border p-4 space-y-4 ${dk.border} ${dk.surfaceMuted}`} data-id="element-19983" data-mp="18zrqebqx6kfb">
                <div className="flex items-center gap-2 mb-1" data-id="element-19984" data-mp="v3e5str3a2wm">
                  <LibraryIcon className="h-4 w-4 text-teal-500" data-id="element-19985" data-mp="zz1w5chb523p" />
                  <span className={`text-sm font-semibold ${dk.textSecondary}`} data-id="element-19986" data-mp="3frsjlepqev1w">
                    Series Connection & Anthology Links
                  </span>
                </div>

                {/* Connection Type */}
                <div data-id="element-19987" data-mp="1q9c3prj40sb9">
                  <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-19988" data-mp="1q9vi4y9jwykj">
                    Connection Type
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-19989" data-mp="1gj0xtzfv891q">
                    {[{
                  value: 'standalone',
                  label: 'Standalone',
                  sub: 'Not part of a series'
                }, {
                  value: 'connected',
                  label: 'Connected Series',
                  sub: 'Must read in order'
                }, {
                  value: 'interconnected',
                  label: 'Interconnected',
                  sub: 'Standalone, shared world'
                }, {
                  value: 'crossover',
                  label: 'Series Crossover',
                  sub: 'Links multiple series'
                }].map(opt => <button key={opt.value} type="button" onClick={() => setConnectionType(connectionType === opt.value ? '' : opt.value)} className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors text-left ${connectionType === opt.value ? 'bg-teal-100 text-teal-700 border-teal-500 dark:bg-teal-900/30 dark:text-teal-300' : `${dk.surfaceMuted} ${dk.textSecondary} ${dk.border} hover:border-teal-400`}`} data-id="element-19990" data-mp="enmssbz89ph7">
                        <div data-id="element-19991" data-mp="1b7vi6ocp0agv">{opt.label}</div>
                        <div className="text-[10px] opacity-60 font-normal" data-id="element-19992" data-mp="1b73yaasgk16f">
                          {opt.sub}
                        </div>
                      </button>)}
                  </div>
                </div>

                {/* Entry Point */}
                <div data-id="element-19993" data-mp="19rhzwni9uhq2">
                  <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-19994" data-mp="19s14z3mtpbmo">
                    Entry Point
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-19995" data-mp="1jmt5ogbpcelo">
                    {[{
                  value: 'standalone',
                  label: 'Can be read as standalone'
                }, {
                  value: 'order',
                  label: 'Must be read in order'
                }].map(opt => <button key={opt.value} type="button" onClick={() => setEntryPoint(entryPoint === opt.value ? '' : opt.value)} className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors
                          ${entryPoint === opt.value ? 'bg-blue-100 text-blue-700 border-blue-500 dark:bg-blue-900/30 dark:text-blue-300' : `${dk.surfaceMuted} ${dk.textSecondary} ${dk.border} hover:border-blue-400`}`} data-id="element-19996" data-mp="22gynn7uy5afy">
                        {opt.label}
                      </button>)}
                  </div>
                </div>

                {/* Crossover Details - shown only if crossover is selected */}
                {connectionType === 'crossover' && <div className={`mt-4 p-4 rounded-lg border border-dashed border-teal-300 dark:border-teal-700 bg-teal-50/50 dark:bg-teal-900/10`} data-id="element-19997" data-mp="27bhvk12uz87o">
                    <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-19998" data-mp="1yb37wv2f9h10">
                      Linked Series
                    </label>
                    <div className="flex gap-2" data-id="element-19999" data-mp="3v9ip5jycx7uy">
                      <select className={`flex-1 p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-20000" data-mp="3v8qs04p4322k">
                        <option value="" data-id="element-20001" data-mp="3q82zfel8id68">Select a series to link...</option>
                        <option value="shadow-files" data-id="element-20002" data-mp="3maovo2gboxlm">Shadow Files</option>
                        <option value="midnight-chronicles" data-id="element-20003" data-mp="msnrqk0bwfo9">
                          Midnight Chronicles
                        </option>
                      </select>
                      <input type="number" placeholder="Reading Order #" className={`w-32 p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-20004" data-mp="dsshqiiieqpt" />
                    </div>
                    <p className={`text-xs mt-2 ${dk.textMuted}`} data-id="element-20005" data-mp="2ykkrntz82vu3">
                      This book will appear in the combined reading order for
                      both series.
                    </p>
                  </div>}

                {/* ─── Anthology Connection ─── */}
                <div className={`pt-4 border-t ${dk.border}`} data-id="element-20006" data-mp="1e1gzsuv7k67m">
                  <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-20007" data-mp="1i2kbr6a4xi3k">
                    Anthology Connection
                  </label>
                  <p className={`text-xs mb-3 ${dk.textMuted}`} data-id="element-20008" data-mp="1rtezkf314pr4">
                    Link this book to an anthology — e.g. if it was originally a
                    short story that became a full novel.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3" data-id="element-20009" data-mp="21rbbh93sjoqa">
                    {[{
                  value: 'extended-from',
                  label: 'Extended From',
                  sub: 'Short story expanded into novel'
                }, {
                  value: 'part-of',
                  label: 'Part Of',
                  sub: 'Appears in this anthology'
                }, {
                  value: 'inspired-by',
                  label: 'Inspired By',
                  sub: 'Loosely connected'
                }, {
                  value: 'companion',
                  label: 'Companion Story',
                  sub: 'Side story or bonus content'
                }, {
                  value: 'spinoff',
                  label: 'Spin-off',
                  sub: 'Characters get their own story'
                }].map(opt => <button key={opt.value} type="button" onClick={() => setAnthologyConnection(anthologyConnection === opt.value ? '' : opt.value)} className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors text-left ${anthologyConnection === opt.value ? 'bg-purple-100 text-purple-700 border-purple-500 dark:bg-purple-900/30 dark:text-purple-300' : `${dk.surfaceMuted} ${dk.textSecondary} ${dk.border} hover:border-purple-400`}`} data-id="element-20010" data-mp="39oxmrwc3g4n2">
                        <div data-id="element-20011" data-mp="1g81nsgcihdoa">{opt.label}</div>
                        <div className="text-[10px] opacity-60 font-normal" data-id="element-20012" data-mp="1l6mkw6wgvh8i">
                          {opt.sub}
                        </div>
                      </button>)}
                  </div>
                  {anthologyConnection && <div className={`p-3 rounded-lg border border-dashed border-purple-300 dark:border-purple-700 bg-purple-50/50 dark:bg-purple-900/10`} data-id="element-20013" data-mp="1beysa2o6bu5g">
                      <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-20014" data-mp="23yf6vhd4c26q">
                        Linked Anthology / Book
                      </label>
                      <input type="text" placeholder="Search for an anthology or book title..." value={anthologySearch} onChange={e => setAnthologySearch(e.target.value)} className={`w-full p-2 rounded-md border text-sm ${dk.inputOnCard}`} data-id="element-20015" data-mp="3g8no9lgw1gj" />
                      {anthologySearch && <div className={`mt-2 p-2 rounded-md border ${dk.border} ${dk.surfaceMuted}`} data-id="element-20016" data-mp="32u2qo63zsqze">
                          <p className={`text-xs ${dk.textMuted}`} data-id="element-20017" data-mp="37uqe6ht0ipov">
                            Matching results will appear here when connected to
                            your catalog.
                          </p>
                          <div className="mt-2 space-y-1" data-id="element-20018" data-mp="1g57icopgjnfg">
                            {['Dark Hearts Anthology', 'Midnight Tales Collection', 'Shadows & Whispers'].filter(a => a.toLowerCase().includes(anthologySearch.toLowerCase())).map(result => <button key={result} type="button" className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${dk.surfaceMuted} hover:bg-purple-50 dark:hover:bg-purple-900/20 ${dk.textSecondary}`} data-id="element-20019" data-mp="1typ1i8e8ey4y">
                                  {result}
                                </button>)}
                          </div>
                        </div>}
                    </div>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-20020" data-mp="2ijfdci2g1nw7">
                <div data-id="element-20021" data-mp="2nis1d651x10d">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20022" data-mp="2njb9jv4wgb17">
                    First Published
                  </label>
                  <input type="date" value={bookForm.publishDate || ''} onChange={e => setBookForm({
                ...bookForm,
                publishDate: e.target.value
              })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-20023" data-mp="2wiy90be2j6rm" />
                </div>
                <div data-id="element-20024" data-mp="3fah8w9csq4pf">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20025" data-mp="3f7lcwqkidq3m">
                    Page Count
                  </label>
                  <input type="number" value={bookForm.pageCount || ''} onChange={e => setBookForm({
                ...bookForm,
                pageCount: parseInt(e.target.value)
              })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="0" data-id="element-20026" data-mp="zjpncs6nd2ip" />
                </div>
              </div>
            </>}

          {productType === 'merch' && <>
              <div data-id="element-20027" data-mp="18lfahlthxfao">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20028" data-mp="18jur8scmtis4">
                  Product Name
                </label>
                <input type="text" value={merchForm.name || ''} onChange={e => setMerchForm({
              ...merchForm,
              name: e.target.value
            })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Enter product name" data-id="element-20029" data-mp="2191olo0co2bs" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-20030" data-mp="24ikuvyoywqdh">
                <div data-id="element-20031" data-mp="29hxp54i64zq3">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20032" data-mp="29i76k3p5dyk6">
                    Category
                  </label>
                  <select value={merchForm.category || ''} onChange={e => setMerchForm({
                ...merchForm,
                category: e.target.value
              })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-20033" data-mp="1zrupilgjknis">
                    <option value="" data-id="element-20034" data-mp="bud3pqgzbvvt">Select Category</option>
                    {merchCategories.map(cat => <option key={cat} value={cat} data-id="element-20035" data-mp="pm9t3mr9jroi">
                        {cat}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-20036" data-mp="3tzyk9f26isoq">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20037" data-mp="3tz6lxwh76gur">
                    Related Book
                  </label>
                  <select value={merchForm.relatedBook || ''} onChange={e => setMerchForm({
                ...merchForm,
                relatedBook: e.target.value
              })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-20038" data-mp="6vs2bnpzr65s">
                    <option value="" data-id="element-20039" data-mp="ay6i0j8hljr9">None</option>
                    {mockBooks.map(book => <option key={book.id} value={book.id} data-id="element-20040" data-mp="fwrd648vtd8u">
                        {book.title}
                      </option>)}
                  </select>
                </div>
              </div>
            </>}

          {productType === 'event' && <>
              <div data-id="element-20041" data-mp="3odco3ql2qgel">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20042" data-mp="3oduhmfgcqgit">
                  Event Title
                </label>
                <input type="text" value={eventForm.title || ''} onChange={e => setEventForm({
              ...eventForm,
              title: e.target.value
            })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Book Signing, Q&A, etc." data-id="element-20043" data-mp="17ldyga1nn0y" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-20044" data-mp="3221iqodmc8us">
                <div data-id="element-20045" data-mp="2x5ub8r5kqpaq">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20046" data-mp="2x381ckv7nxpc">
                    Event Type
                  </label>
                  <select value={eventForm.type || ''} onChange={e => setEventForm({
                ...eventForm,
                type: e.target.value
              })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-20047" data-mp="2o4cz1jvaf7rb">
                    <option value="" data-id="element-20048" data-mp="24hydrk86ktcw">Select Type</option>
                    {eventTypes.map(t => <option key={t} value={t} data-id="element-20049" data-mp="20eiml3evrl7l">
                        {t}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-20050" data-mp="3jdomxii2mxq6">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20051" data-mp="3jd6g5rxs9140">
                    Capacity
                  </label>
                  <input type="number" value={eventForm.capacity || ''} onChange={e => setEventForm({
                ...eventForm,
                capacity: parseInt(e.target.value)
              })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="0" data-id="element-20052" data-mp="3ab6zj0qojz1l" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-20053" data-mp="2vnu8iwi3t13t">
                <div data-id="element-20054" data-mp="2hn1dmanw4406">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20055" data-mp="2hom6zwygxgwg">
                    Start Date
                  </label>
                  <input type="date" value={eventForm.startDate || ''} onChange={e => setEventForm({
                ...eventForm,
                startDate: e.target.value
              })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-20056" data-mp="231nk8fak3ao2" />
                </div>
                <div data-id="element-20057" data-mp="2lxdm82iho71y">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20058" data-mp="2h0n83kq8vlj6">
                    End Date
                  </label>
                  <input type="date" value={eventForm.endDate || ''} onChange={e => setEventForm({
                ...eventForm,
                endDate: e.target.value
              })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-20059" data-mp="2vr2uh2ueczix" />
                </div>
                <div data-id="element-20060" data-mp="yauwx2r7exg">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20061" data-mp="z3t39nncw3z">
                    Time
                  </label>
                  <input type="text" value={eventForm.time || ''} onChange={e => setEventForm({
                ...eventForm,
                time: e.target.value
              })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="e.g. 2:00 PM - 4:00 PM" data-id="element-20062" data-mp="3n4qz891b4vvt" />
                </div>
              </div>
              <div data-id="element-20063" data-mp="2zewnjusq5dd5">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20064" data-mp="2zfeqfa9w6pu1">
                  Location
                </label>
                <input type="text" value={eventForm.location || ''} onChange={e => setEventForm({
              ...eventForm,
              location: e.target.value
            })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Address or Venue" data-id="element-20065" data-mp="1avsfp6lyq4hi" />
              </div>
              <div className="flex items-center gap-2" data-id="element-20066" data-mp="w7ecuvojqg0y">
                <input type="checkbox" id="isVirtual" checked={eventForm.isVirtual || false} onChange={e => setEventForm({
              ...eventForm,
              isVirtual: e.target.checked
            })} className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" data-id="element-20067" data-mp="w7xltmw7xu2u" />
                <label htmlFor="isVirtual" className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-20068" data-mp="5fp25gh3469o">
                  This is a virtual event
                </label>
              </div>
              {eventForm.isVirtual && <div data-id="element-20069" data-mp="a771k03glysj">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20070" data-mp="a6xbkciezrmu">
                    Virtual Link
                  </label>
                  <input type="text" value={eventForm.virtualLink || ''} onChange={e => setEventForm({
              ...eventForm,
              virtualLink: e.target.value
            })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Zoom/Meet URL" data-id="element-20071" data-mp="3elx5c1b1hucl" />
                </div>}
            </>}

          {productType === 'bookbox' && <>
              <div data-id="element-20072" data-mp="2psc4jizdruwz">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20073" data-mp="2ptna9ocdzcqq">
                  Box Name
                </label>
                <input type="text" value={bookBoxForm.boxName || ''} onChange={e => setBookBoxForm({
              ...bookBoxForm,
              boxName: e.target.value
            })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="e.g. Fantasy Realm Crate" data-id="element-20074" data-mp="2grmcmqd8qooj" />
              </div>
              <div data-id="element-20075" data-mp="2zof8mudsm3mk">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20076" data-mp="3ngm842eteqq7">
                  Theme
                </label>
                <input type="text" value={bookBoxForm.theme || ''} onChange={e => setBookBoxForm({
              ...bookBoxForm,
              theme: e.target.value
            })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="e.g. Enemies to Lovers" data-id="element-20077" data-mp="3s8vx9g83vmrf" />
              </div>
            </>}

          {productType === 'creative' && <>
              <div data-id="element-20078" data-mp="2ibfn63n29351">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20079" data-mp="2iaxs3ac4wuyp">
                  Display Name
                </label>
                <input type="text" value={creativeForm.displayName || ''} onChange={e => setCreativeForm({
              ...creativeForm,
              displayName: e.target.value
            })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Your Name or Studio Name" data-id="element-20080" data-mp="28jjp2skz5d0t" />
              </div>
              <div data-id="element-20081" data-mp="3ih9l6pknqzty">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20082" data-mp="3igrjvej51vf6">
                  Role
                </label>
                <input type="text" value={creativeForm.role || ''} onChange={e => setCreativeForm({
              ...creativeForm,
              role: e.target.value
            })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="e.g. Cover Designer, Illustrator" data-id="element-20083" data-mp="3rjskx0t5cwbz" />
              </div>
            </>}

          {productType === 'service' && <>
              <div data-id="element-20084" data-mp="2f6c3cznmkjiw">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20085" data-mp="2f5kgqec09w4r">
                  Display Name
                </label>
                <input type="text" value={serviceForm.displayName || ''} onChange={e => setServiceForm({
              ...serviceForm,
              displayName: e.target.value
            })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Your Name or Business Name" data-id="element-20086" data-mp="2ovvko0vow5bn" />
              </div>
              <div data-id="element-20087" data-mp="32vwx8dlbh16y">
                <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20088" data-mp="32vesstoi3gny">
                  Role
                </label>
                <select value={serviceForm.role || ''} onChange={e => setServiceForm({
              ...serviceForm,
              role: e.target.value
            })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} data-id="element-20089" data-mp="1ditelzr7uy5q">
                  <option value="" data-id="element-20090" data-mp="3bcfevu2qam93">Select Role</option>
                  {serviceProviderRoles.map(role => <option key={role} value={role} data-id="element-20091" data-mp="3fcpmtca2zmql">
                      {role}
                    </option>)}
                </select>
              </div>
            </>}

          {/* Description for all types */}
          <div data-id="element-20092" data-mp="2sekwwqjmh0ek">
            <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20093" data-mp="2sf4gscsu1yv8">
              {productType === 'creative' || productType === 'service' ? 'Bio' : 'Description'}
            </label>
            <textarea rows={5} value={productType === 'book' ? bookForm.description : productType === 'merch' ? merchForm.description : productType === 'event' ? eventForm.description : productType === 'bookbox' ? bookBoxForm.description : productType === 'creative' ? creativeForm.bio : serviceForm.bio || ''} onChange={e => {
            const val = e.target.value;
            if (productType === 'book') setBookForm({
              ...bookForm,
              description: val
            });else if (productType === 'merch') setMerchForm({
              ...merchForm,
              description: val
            });else if (productType === 'event') setEventForm({
              ...eventForm,
              description: val
            });else if (productType === 'bookbox') setBookBoxForm({
              ...bookBoxForm,
              description: val
            });else if (productType === 'creative') setCreativeForm({
              ...creativeForm,
              bio: val
            });else setServiceForm({
              ...serviceForm,
              bio: val
            });
          }} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Enter description..." data-id="element-20094" data-mp="uob9esfcowlu" />
          </div>

          {productType === 'bookbox' && <div data-id="element-20095" data-mp="1p370plyeaabi">
              <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20096" data-mp="1p3q8way8tkcc">
                Contents Overview
              </label>
              <textarea rows={3} value={bookBoxForm.contentsOverview || ''} onChange={e => setBookBoxForm({
            ...bookBoxForm,
            contentsOverview: e.target.value
          })} className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} placeholder="Brief overview of what's inside..." data-id="element-20097" data-mp="1reyeukf1q26r" />
            </div>}
        </div>
      </div>
    </div>;
};